/**
 * Wishlist-UI: Heart-Icon-Toggle auf Cards + Drawer im Sticky-Header.
 */
import { useEffect, useState } from "react";
import { Heart, Lock, Trash2, X } from "lucide-react";
import { useWishlist } from "../lib/discovery_store";

type ThemeLike = {
  accent: string;
  ink: string;
  inkMuted: string;
  cardBg: string;
};

export function WishlistButton({
  agentCode,
  theme,
  isDark,
}: {
  agentCode: string;
  theme: ThemeLike;
  isDark: boolean;
}) {
  const [open, setOpen] = useState(false);
  const { count } = useWishlist(agentCode);
  if (count === 0) return null;
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="relative inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold border"
        style={{
          color: theme.ink,
          background: theme.cardBg,
          borderColor: isDark ? "rgba(255,255,255,.10)" : "rgba(0,0,0,.10)",
        }}
        aria-label={`Merkliste mit ${count} Artikeln`}
      >
        <Heart className="size-4 fill-rose-500 text-rose-500" />
        <span className="hidden md:inline">Merkliste</span>
        <span
          className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] grid place-items-center rounded-full text-[10px] font-bold px-1"
          style={{ background: theme.accent, color: "#fff" }}
        >
          {count}
        </span>
      </button>
      {open && (
        <WishlistDrawer
          agentCode={agentCode}
          theme={theme}
          isDark={isDark}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function WishlistDrawer({
  agentCode,
  theme,
  isDark,
  onClose,
}: {
  agentCode: string;
  theme: ThemeLike;
  isDark: boolean;
  onClose: () => void;
}) {
  const { items, remove } = useWishlist(agentCode);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[60]" aria-modal="true" role="dialog">
      <div
        className="absolute inset-0"
        style={{ background: "rgba(0,0,0,0.55)" }}
        onClick={onClose}
      />
      <div
        className="absolute top-0 right-0 h-full w-full sm:max-w-md flex flex-col shadow-2xl"
        style={{ background: theme.cardBg, color: theme.ink, animation: "dp-wl-slide-in 0.25s ease-out both" }}
      >
        <style>{`@keyframes dp-wl-slide-in { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
        <div
          className="flex items-center justify-between px-4 py-3 border-b"
          style={{ borderColor: isDark ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.08)" }}
        >
          <div className="flex items-center gap-2">
            <Heart className="size-5 fill-rose-500 text-rose-500" />
            <h3 className="text-base font-semibold">Deine Merkliste</h3>
            <span className="text-xs" style={{ color: theme.inkMuted }}>
              ({items.length})
            </span>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 hover:bg-current/5" aria-label="Schließen">
            <X className="size-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-2 py-2">
          {items.length === 0 ? (
            <div className="grid place-items-center h-full text-center px-6">
              <p className="text-sm" style={{ color: theme.inkMuted }}>
                Noch nichts auf der Merkliste.
              </p>
            </div>
          ) : (
            <ul className="space-y-1.5">
              {items.map((it) => (
                <li
                  key={it.wc_product_id}
                  className="flex gap-3 p-2 rounded-xl"
                  style={{ background: isDark ? "rgba(255,255,255,.03)" : "rgba(0,0,0,.02)" }}
                >
                  <div
                    className="size-16 rounded-lg overflow-hidden flex-shrink-0 grid place-items-center"
                    style={{ background: isDark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.04)" }}
                  >
                    {it.image_url ? (
                      <img src={it.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <Heart className="size-5 opacity-30" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <a
                      href={`/p/${it.wc_product_id}`}
                      className="text-sm font-medium leading-snug line-clamp-2 hover:underline"
                    >
                      {it.name}
                    </a>
                    {it.price ? (
                      <div className="text-sm font-bold mt-0.5" style={{ color: theme.accent }}>
                        {it.price} €
                      </div>
                    ) : (
                      <div className="text-[11px] mt-0.5 inline-flex items-center gap-1" style={{ color: theme.inkMuted }}>
                        <Lock className="size-3" />
                        Preis nach Login
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <a
                      href={`/p/${it.wc_product_id}`}
                      className="rounded-lg px-2 py-1 text-[11px] font-semibold"
                      style={{ background: theme.accent, color: "#fff" }}
                    >
                      Ansehen
                    </a>
                    <button
                      onClick={() => remove(it.wc_product_id)}
                      className="text-current/40 hover:text-rose-600"
                      aria-label="Entfernen"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Heart-Toggle für Product-Cards. Positioniert absolute, deshalb braucht der
 * Parent `relative`. Stop-Propagation damit der Card-Link nicht triggert.
 */
export function WishlistHeart({
  agentCode,
  product,
  size = "md",
}: {
  agentCode: string;
  product: {
    wc_product_id: number;
    name: string;
    image_url: string | null;
    price: string | null;
    permalink: string;
  };
  theme?: ThemeLike;
  size?: "sm" | "md";
}) {
  const { has, toggle } = useWishlist(agentCode);
  if (!product.wc_product_id) return null;
  const active = has(product.wc_product_id);
  const sz = size === "sm" ? "size-7" : "size-9";
  const iconSz = size === "sm" ? "size-3.5" : "size-4";
  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        toggle(product);
      }}
      className={`absolute top-2 left-2 ${sz} grid place-items-center rounded-full backdrop-blur-sm transition-transform hover:scale-110 z-10`}
      style={{
        background: active ? "rgba(244,63,94,0.95)" : "rgba(255,255,255,0.92)",
      }}
      aria-label={active ? "Von Merkliste entfernen" : "Zur Merkliste hinzufügen"}
      title={active ? "Auf Merkliste" : "Merken"}
    >
      <Heart
        className={`${iconSz} transition-colors ${active ? "fill-white text-white" : "text-rose-600"}`}
      />
    </button>
  );
}
